import { useTheme } from '@react-navigation/native'
import { router } from 'expo-router'
import { useCallback, useMemo, useState } from 'react'
import { ActivityIndicator, Platform, Pressable, ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { Badge, BadgeText } from '@/components/ui/badge'
import { Button, ButtonText } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Text } from '@/components/ui/text'
import useDefaultToast from '@/components/useDefaultToast'
import { getAuthToken } from '@/lib/api/authToken'
import { PLANS, Plan, isPlanCodeGtOther } from '@/lib/payment/plans'
import formatCurrency from '@/lib/utils/formatCurrency'
import PlatformDetection from '@/lib/utils/platformDetection'
import { selectCurrentBillingAccount } from '@/redux/slices/currentUserSlice'
import { useAppSelector } from '@/redux/store'
import { BillingAccount, PlanCode } from '@/typings/domain'

type CashierParams = {
  title: string
  cashierType: 'plan' | 'recharge' | 'vas'
  amount: number
  planCode: PlanCode
  payType: 'per_year' | 'per_month'
  quantity: number
}

type PlansProps = {
  recommendedPlanCode?: PlanCode
}

export default function Plans({ recommendedPlanCode: recommendedPlanCodeProp }: PlansProps = {}) {
  const insets = useSafeAreaInsets()
  const { user: currentUser, isOwnerOrAdmin } = useAppSelector(state => state.currentUser)
  const billingAccount = useAppSelector(selectCurrentBillingAccount)
  const usageAndBalanceLoaded = useAppSelector(state => state.usageAndBalance.loaded)

  const currentPlanCode = billingAccount?.planPackage?.plan.code as PlanCode | undefined
  const currentPlan = PLANS.find(plan => plan.code === currentPlanCode)
  const publiclyPlans = useMemo(() => PLANS.filter(plan => plan.isListedPublicly), [])

  const defaultRecommendedPlanCode = isPlanCodeGtOther(currentPlan?.code, PlanCode.E10)
    ? currentPlan?.code
    : PlanCode.E10
  const recommendedPlanCode = recommendedPlanCodeProp || defaultRecommendedPlanCode

  if (!usageAndBalanceLoaded || !currentUser || !billingAccount) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    )
  }

  return (
    <ScrollView
      contentInsetAdjustmentBehavior="automatic"
      className="bg-background-0"
      contentContainerStyle={Platform.OS === 'android' ? { paddingBottom: insets.bottom } : undefined}
    >
      <View className="w-full p-6 gap-4">
        <Text className="mb-0 text-secondary-label">根据你的需求，选择最佳方案</Text>
        <View className="gap-6 w-full">
          {publiclyPlans.map(plan => (
            <PlanCard
              key={plan.code}
              plan={plan}
              plans={publiclyPlans}
              currentPlan={currentPlan}
              billingAccount={billingAccount as BillingAccount}
              isRecommended={plan.code === recommendedPlanCode}
              canPurchase={isOwnerOrAdmin}
            />
          ))}
        </View>
      </View>
    </ScrollView>
  )
}

type PlanCardProps = {
  plan: Plan
  plans: Plan[]
  currentPlan?: Plan
  billingAccount: BillingAccount
  isRecommended: boolean
  canPurchase: boolean
}

function PlanCard({ plan, plans, currentPlan, billingAccount, isRecommended, canPurchase }: PlanCardProps) {
  const theme = useTheme()
  const toast = useDefaultToast()
  const [payType, setPayType] = useState<'per_year' | 'per_month'>('per_year')
  const supportMonthly = useMemo(
    () => billingAccount.monthlyPayablePlans.includes(plan.code) && typeof plan.pricePerMonth === 'number',
    [billingAccount.monthlyPayablePlans, plan.code, plan.pricePerMonth]
  )

  const planIndex = plans.findIndex(p => p.code === plan?.code)
  const showMonthly = supportMonthly && payType === 'per_month'
  const isCurrentPlan = plan.code === currentPlan?.code

  const amount = showMonthly ? plan.pricePerMonth! : plan.pricePerYear

  const handlePurchase = useCallback(() => {
    if (!canPurchase) {
      toast.show('请联系管理员升级套餐')
      return
    }
    const cashierParams: CashierParams = {
      title: plan.title,
      cashierType: 'plan',
      amount,
      planCode: plan.code,
      payType,
      quantity: 1,
    }

    const paramsBase64 = btoa(encodeURIComponent(JSON.stringify(cashierParams)))

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const platform = new PlatformDetection(navigator.userAgent)
      if (platform.isWeixinBrowser()) {
        const urlParams = new URLSearchParams(window.location.search)
        const code = urlParams.get('code')
        if (code) {
          router.push({
            pathname: '/profile/cashier',
            params: { cashierParams: paramsBase64, code },
          })
          return
        }
        const token = getAuthToken()
        const destPage = encodeURIComponent(`/profile/cashier?token=${token}&cashierParams=${paramsBase64}`)
        window.location.href = `${process.env.EXPO_PUBLIC_API_BASE_URL}/signup/mobile_web_loading?token=${token}&dest_page=${destPage}`
        return
      }
    }

    router.push({
      pathname: '/profile/cashier',
      params: { cashierParams: paramsBase64 },
    })
  }, [canPurchase, plan.title, plan.code, amount, payType, toast])

  return (
    <View className="relative">
      {isRecommended && (
        <View
          className="absolute -top-2 right-4 z-10 bg-system-orange px-3 py-1 rounded-full"
          style={{ transform: [{ translateY: 0 }] }}
        >
          <Text size="footnote" className="font-medium text-label">
            推荐
          </Text>
        </View>
      )}
      <Card
        className={`p-6 rounded-2xl ${isRecommended ? 'border border-primary-500 bg-primary-50 dark:bg-primary-900' : 'border border-typography-100 dark:border-typography-700'}`}
        variant="outline"
      >
        <View className="gap-2">
          <View className="flex-row items-center gap-2">
            <Text size="title-3" className="text-label">
              {plan.title}
            </Text>
            {isCurrentPlan && (
              <Badge action="info" variant="outline" size="sm" className="rounded">
                <BadgeText>当前套餐</BadgeText>
              </Badge>
            )}
          </View>

          <View>
            <Text>
              <Text size="title-1" className="text-label">
                {showMonthly
                  ? formatCurrency({ value: plan.pricePerMonth!, maximumFractionDigits: 0 })
                  : formatCurrency({ value: plan.pricePerYear, maximumFractionDigits: 0 })}
              </Text>
              <Text size="subhead"> /{showMonthly ? '月' : '年'}</Text>
            </Text>
          </View>

          <View>
            {supportMonthly ? (
              <View className="flex-row items-center gap-2">
                <View className="rounded-md flex-row bg-background-muted">
                  <Pressable
                    onPress={() => setPayType('per_year')}
                    className={`py-1 px-3 rounded-md ${payType === 'per_year' ? 'bg-primary-100 ' : 'bg-transparent'}`}
                  >
                    <Text size="footnote" className={payType === 'per_year' ? 'text-primary-600' : 'text-label'}>
                      年付
                    </Text>
                  </Pressable>
                  <Pressable
                    onPress={() => setPayType('per_month')}
                    className={`py-1 px-3 rounded ${
                      payType === 'per_month' ? 'bg-primary-100 border-primary-500' : 'bg-transparent border-border-300'
                    }`}
                  >
                    <Text size="footnote" className={payType === 'per_month' ? 'text-primary-600' : 'text-label'}>
                      月付
                    </Text>
                  </Pressable>
                </View>
                {supportMonthly && payType === 'per_year' && plan.perYearSavingTips && (
                  <Badge action="success" variant="solid" size="sm">
                    <BadgeText>{plan.perYearSavingTips}</BadgeText>
                  </Badge>
                )}
              </View>
            ) : (
              <>
                {plan?.description && (
                  <Text size="subhead" className="text-secondary-label">
                    {plan.description}
                  </Text>
                )}
              </>
            )}
          </View>

          {plan.code === PlanCode.EnCor ? (
            <Text size="subhead" className="text-secondary-label mt-4 mb-4">
              请联系我们{' '}
              <Text size="subhead" className="text-primary-600">
                support@jinshuju.net
              </Text>
            </Text>
          ) : (
            <Button
              onPress={handlePurchase}
              action={isRecommended ? 'primary' : 'secondary'}
              variant={isRecommended ? 'solid' : 'outline'}
              isDisabled={!canPurchase || (currentPlan && plan.weight < currentPlan.weight)}
              size="lg"
              className="rounded-md mt-4 mb-4"
            >
              <ButtonText>{isCurrentPlan ? '续费套餐' : '升级套餐'}</ButtonText>
            </Button>
          )}

          <View className="gap-2">
            <Text size="body" className="text-label">
              {plan.code === PlanCode.Pro2 ? '包含以下高级功能：' : `包含${plans[planIndex - 1]?.title}功能，以及：`}
            </Text>
            {plan.features.map((feature, i) => (
              <View key={i} className="flex-row gap-2 items-center">
                <feature.icon size={14} color={theme.colors.text} />
                <Text size="subhead" className="flex-1 text-secondary-label">
                  {feature.text}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </Card>
    </View>
  )
}
